import { Type } from '@/interfaces/donation/donationType';
<template>
  <v-card class="donation-flow" :class="compact && 'elevation-0 pa-0'" style="padding: 40px;border-radius: 16px;">
     <v-card-title
      :class="compact && 'pl-0 pt-0'"
      class="text-center mx-auto d-flex justify-space-between"
      data-cy="caseview-title"
      style="padding: 0;line-height: 1;"
    >
      <span class="donation-flow__step-title" >
        {{ t('titles.donorDetails') }}
      </span>
      <span class="step-indicator">{{ t('donationGuide.progress.mobileText', { current: 1, total: 3 }) }}</span>
      
    </v-card-title>
    <v-card-text :class="compact && 'pa-0'" style="padding: 0px;">
      <v-form ref="form" v-model="valid" data-cy="donor-detailsview-form" lazy-validation>
        <basic-donor-form-component
          v-if="
            isContribution ||
            (requestSession.skipDonorPersonalIdentityNumber && !!requestSession.externalAgent)
          "
          v-model="donor"
          data-cy="donor-detailsview-basic-form-component"
        />
        <donor-form-component
          v-else-if="isMemoryGift"
          v-model="donor"
          v-model:manual="manualInput"
          v-model:error="showError"
          data-cy="donor-detailsview-form-component"
        />
      </v-form>
    </v-card-text>
    <donation-flow-buttons
      extra-class="pa-0"
      :show-previous="showPreviousButton"
      previous-data-cy="case-view-back-btn"
      next-data-cy="case-view-next-btn"
      next-id="caseViewNextButton"
      @previous-step="handlePrevious"
      @next-step="next"
    />
  </v-card>
</template>

<script lang="ts">
import { defineComponent, reactive, computed, ref, watch, nextTick,onMounted } from 'vue';
import { useLabel } from '@/composables/useLabel';
import store from '@/store';
import { scrollToFirstInvalid } from '@/utils/formHelpers';
import { mask } from 'vue-the-mask';
import { Donation } from '@/interfaces/donation/donation';
import { Donor } from '@/interfaces/donation/donor';
import DonorFormComponent from '@/components/donor/DonorFormComponent.vue';
import BasicDonorFormComponent from '@/components/donor/BasicDonorFormComponent.vue';
import DonationFlowButtons from '@/components/DonationFlowButtons.vue';
import { RequestSession } from '@/interfaces/requestSession';
import { isMemoryGift, isContribution } from '@/classes/DonationType';
import { stepV1, stepV2 } from '@/enums/donationStepOrder';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import isEmail from 'validator/lib/isEmail';
import matomo from '@/services/matomoService';

export default defineComponent({
  components: {
    DonorFormComponent,
    BasicDonorFormComponent,
    DonationFlowButtons,
  },
  directives: {
    mask,
  },
  props: {
    showPreviousButton: {
      type: Boolean,
      default: true,
    },
    compact: Boolean,
  },
  emits: ['previousStep', 'nextStep'],
  setup(props, { emit }) {
    const { t } = useLabel();
    // using top-level store import rather than instance proxy
    const form = ref();

    const data = reactive({
      valid: true,
      manualInput: false,
      showError: false,
      pep: false,
      anonymousDonor: false,
      donor: {
        address: {
          address1: '',
          address2: '',
          address3: '',
          city: '',
          country: '',
          postcode: '',
        },
        email: '',
        firstname: '',
        surname: '',
        socialSecurityNumber: '',
        token: '',
      } as Donor,
    });

    const steps = computed(() => {
      return getDonationStepFlowVersion() == DonationGuideVersion.V1 ? stepV1 : stepV2;
    });

    const donation = computed(() => {
      return store.state.donation as Donation;
    });

    const isMemoryGiftComputed = computed(() => {
      return donation.value && isMemoryGift(donation.value.donationType.type);
    });

    const isContributionComputed = computed(() => {
      return donation.value && isContribution(donation.value.donationType.type);
    });

    const requestSession = computed(() => {
      return store.state.requestSession as RequestSession;
    });

    const getDonationStepFlowVersion = () => {
      return store.state.donationGuideVersion || DonationGuideVersion.V1;
    };

    watch(donation, () => {
      data.valid = true;
      data.manualInput = false;
      data.showError = false;
      data.pep = false;
      data.anonymousDonor = false;
      data.donor = {
        address: { address1: '', address2: '', address3: '', city: '', country: '', postcode: '' },
        email: '',
        firstname: '',
        surname: '',
        socialSecurityNumber: '',
        token: '',
      } as Donor;
    });

    // Watch for anonymous donor checkbox changes
    watch(
      () => data.anonymousDonor,
      (newVal: boolean) => {
        if (newVal) {
          // Clear the donor name fields when anonymous is checked
          data.donor.firstname = '';
          data.donor.surname = '';
        }
        nextTick(() => {
          form.value?.validate();
        });
      }
    );

    onMounted(() => {
      matomo.trackEvent('Contribution', 'Viewed', 'Personal Information Screen');
    });

    const handlePrevious = () => {
      matomo.trackEvent('Contribution', 'Clicked', 'Previous');
      emit('previousStep');
    };

    const next = async () => {
      // Manual validation - check required fields directly
      matomo.trackEvent('Contribution', 'Clicked', 'Next');
      const isMemoryGiftType = donation.value && isMemoryGift(donation.value.donationType.type);

      let isValid = true;
      let firstInvalidField: string | null = null;
      let missingFields: string[] = [];

      // Email: required in general, but OPTIONAL for contribution flow when an organizationGroup is present
      const contributionWithOrgGroup =
        isContributionComputed.value &&
        isContributionComputed.value &&
        requestSession &&
        requestSession.value &&
        requestSession.value.organizationGroup;

      if (contributionWithOrgGroup) {
        // In org-group contribution flow email may be omitted; if provided it must be valid
        if (data.donor.email && data.donor.email.trim() && !isEmail(data.donor.email)) {
          matomo.trackEvent('Contribution', 'Validation Error', 'Invalid Email');
          isValid = false;
          missingFields.push(t('validation.email'));
          if (!firstInvalidField) firstInvalidField = 'email';
        }
      } else {
        if (!data.donor.email || !data.donor.email.trim()) {
          isValid = false;
          missingFields.push(t('donorDetails.form.email.label'));
          if (!firstInvalidField) firstInvalidField = 'email';
        } else if (!isEmail(data.donor.email)) {
          isValid = false;
          missingFields.push(t('validation.email'));
          if (!firstInvalidField) firstInvalidField = 'email';
        }
      }

      // For memory gifts with manual input, personal info is required
      // For contributions, donor info is optional (can remain anonymous)
      if (isMemoryGiftType && data.manualInput) {
        if (!data.donor.firstname || !data.donor.firstname.trim()) {
          isValid = false;
          missingFields.push(t('donorDetails.form.firstname.label'));
          if (!firstInvalidField) firstInvalidField = 'firstname';
        }
        if (!data.donor.surname || !data.donor.surname.trim()) {
          isValid = false;
          missingFields.push(t('donorDetails.form.lastname.label'));
          if (!firstInvalidField) firstInvalidField = 'surname';
        }
        if (!data.donor.address.address1 || !data.donor.address.address1.trim()) {
          isValid = false;
          missingFields.push(t('donorDetails.form.address.label'));
          if (!firstInvalidField) firstInvalidField = 'address1';
        }
        if (!data.donor.address.postcode || !data.donor.address.postcode.trim()) {
          isValid = false;
          missingFields.push(t('donorDetails.form.postcode.label'));
          if (!firstInvalidField) firstInvalidField = 'postcode';
        }
        if (!data.donor.address.city || !data.donor.address.city.trim()) {
          isValid = false;
          missingFields.push(t('donorDetails.form.city.label'));
          if (!firstInvalidField) firstInvalidField = 'city';
        }
      }

      if (!isValid) {
        // Scroll to first invalid field
        if (firstInvalidField) {
          const element = document.querySelector(`[data-cy*="${firstInvalidField}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        // Show validation errors on the form
        data.showError = true;
        // Also trigger Vuetify validation to show field-specific messages
        try {
          if (form.value && typeof form.value.validate === 'function') {
            const res = form.value.validate();
            if (res && typeof res.then === 'function') {
              await Promise.resolve(res);
            }
          }
        } catch {
          // ignore
        }
        return;
      }

      // Ensure Vuetify form-level validation passes
      try {
        if (form.value && typeof form.value.validate === 'function') {
          const result = form.value.validate();
          const validForm =
            result && typeof result.then === 'function' ? await Promise.resolve(result) : !!result;
          if (!validForm) {
            data.showError = true;
            try {
              scrollToFirstInvalid(form.value, null);
            } catch {
              // no-op
            }
            return;
          }
        }
      } catch {
        data.showError = true;
        return;
      }

      // At this point manual checks and form validation passed
      if (data.manualInput) {
        data.donor.token = '';
      }
      const donationVal: Donation = donation.value;
      donationVal.donor = data.donor;
      store.commit('updateDonation', donationVal);
      emit('nextStep');
    };

    return {
      t,
      form,
      ...data,
      steps,
      donation,
      isMemoryGift: isMemoryGiftComputed,
      isContribution: isContributionComputed,
      requestSession,
      handlePrevious,
      next,
    };
  },
});
</script>
